import { logger } from "../../utils/logger";
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { Plus, Edit2, Trash2, PlaySquare, Users } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyBoard = {
  game_type: "",
  stakes: "",
  seats_available: 0,
  table_number: "",
  is_active: true,
};

export default function LiveBoardManager() {
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBoard, setEditingBoard] = useState(null);
  const [formData, setFormData] = useState(emptyBoard);
  const { getAuthHeaders } = useAuth();

  const fetchBoards = async () => {
    try {
      const response = await axios.get(`${API}/live-board/all`, getAuthHeaders());
      setBoards(response.data);
    } catch (err) {
      logger.error("Error fetching boards:", err);
      toast.error("Failed to load live board");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBoards();
  }, []);

  const openCreateDialog = () => {
    setEditingBoard(null);
    setFormData(emptyBoard);
    setDialogOpen(true);
  };

  const openEditDialog = (board) => {
    setEditingBoard(board);
    setFormData({
      game_type: board.game_type,
      stakes: board.stakes,
      seats_available: board.seats_available,
      table_number: board.table_number || "",
      is_active: board.is_active,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.game_type || !formData.stakes) {
      toast.error("Please fill in required fields");
      return;
    }

    try {
      if (editingBoard) {
        await axios.put(`${API}/live-board/${editingBoard.id}`, formData, getAuthHeaders());
        toast.success("Board entry updated!");
      } else {
        await axios.post(`${API}/live-board`, formData, getAuthHeaders());
        toast.success("Board entry created!");
      }
      setDialogOpen(false);
      fetchBoards();
    } catch (err) {
      toast.error("Failed to save board entry");
    }
  };

  const handleDelete = async (boardId) => {
    if (!window.confirm("Are you sure you want to delete this entry?")) return;

    try {
      await axios.delete(`${API}/live-board/${boardId}`, getAuthHeaders());
      toast.success("Entry deleted!");
      fetchBoards();
    } catch (err) {
      toast.error("Failed to delete entry");
    }
  };

  if (loading) {
    return <div className="text-gold animate-pulse">Loading live board...</div>;
  }

  return (
    <div data-testid="live-board-manager">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl text-white mb-2">Live Poker Board</h1>
          <p className="text-white/60">Manage active cash games and table availability</p>
        </div>
        <Button
          onClick={openCreateDialog}
          className="bg-gold text-black hover:bg-gold-dark"
          data-testid="add-board-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Game
        </Button>
      </div>

      {/* Board List */}
      {boards.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <PlaySquare className="w-12 h-12 text-gold/30 mx-auto mb-4" />
          <p className="text-white/60">No active games. Add your first live game!</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {boards.map((board) => (
            <div
              key={board.id}
              className={`glass-card p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${!board.is_active ? 'opacity-50' : ''}`}
              data-testid={`board-item-${board.id}`}
            >
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="font-heading text-xl text-white">{board.game_type}</h3>
                  <span className="px-2 py-1 bg-gold/20 text-gold text-xs rounded">
                    {board.stakes}
                  </span>
                  {board.table_number && (
                    <span className="text-white/50 text-sm">Table {board.table_number}</span>
                  )}
                </div>
                <div className="flex items-center gap-2 text-white/60">
                  <Users className="w-4 h-4" />
                  <span>{board.seats_available} seats available</span>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openEditDialog(board)}
                  className="border-white/20 text-white hover:border-gold hover:text-gold"
                  data-testid={`edit-board-${board.id}`}
                >
                  <Edit2 className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDelete(board.id)}
                  className="border-white/20 text-white hover:border-red-500 hover:text-red-500"
                  data-testid={`delete-board-${board.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-casino-dark border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {editingBoard ? "Edit Game" : "Add Game"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-4" data-testid="board-form">
            <div>
              <Label className="text-white/80">Game Type *</Label>
              <Input
                value={formData.game_type}
                onChange={(e) => setFormData({ ...formData, game_type: e.target.value })}
                className="bg-casino-secondary border-white/10 text-white mt-1"
                placeholder="e.g., No Limit Hold'em"
                data-testid="game-type-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white/80">Stakes *</Label>
                <Input
                  value={formData.stakes}
                  onChange={(e) => setFormData({ ...formData, stakes: e.target.value })}
                  className="bg-casino-secondary border-white/10 text-white mt-1"
                  placeholder="e.g., $1/$2"
                  data-testid="stakes-input"
                />
              </div>
              <div>
                <Label className="text-white/80">Seats Available</Label>
                <Input
                  type="number"
                  value={formData.seats_available}
                  onChange={(e) => setFormData({ ...formData, seats_available: parseInt(e.target.value) || 0 })}
                  className="bg-casino-secondary border-white/10 text-white mt-1"
                  min="0"
                  max="10"
                  data-testid="seats-input"
                />
              </div>
            </div>

            <div>
              <Label className="text-white/80">Table Number (optional)</Label>
              <Input
                value={formData.table_number}
                onChange={(e) => setFormData({ ...formData, table_number: e.target.value })}
                className="bg-casino-secondary border-white/10 text-white mt-1"
                placeholder="e.g., 1"
                data-testid="table-input"
              />
            </div>

            <div className="flex items-center justify-between py-2">
              <Label className="text-white/80">Active</Label>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                data-testid="board-active-switch"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="flex-1 border-white/20 text-white"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-gold text-black hover:bg-gold-dark"
                data-testid="board-submit-btn"
              >
                {editingBoard ? "Update" : "Add Game"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
